import { Helmet } from "react-helmet-async";
import AdvancedSEOHead from "@/components/seo/AdvancedSEOHead";
import StructuredData, { ServiceSchema, FAQ } from "@/components/seo/StructuredData";
import LLMFriendlyContent, { defaultPageFacts } from "@/components/seo/LLMFriendlyContent";
import { pageSEO } from "@/lib/seo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ServicesTriad } from "@/components/ServicesTriad";
import { CheckCircle, ArrowRight, Zap, Target, TrendingUp, Shield } from "lucide-react";
import { Link } from "react-router-dom";
import { SummaryBlock } from "@/components/summary/SummaryBlock";
import { useCta } from "@/hooks/useCta";
import PartnerBadge from '@/components/badges/PartnerBadge';

// Services schema for structured data
const servicesData: ServiceSchema[] = [
  {
    name: "Audit Amazon Gratuit",
    description: "Audit complet de votre compte Amazon avec plan d'action priorisé et recommandations d'experts SPN",
    serviceType: "Amazon Consulting",
    priceRange: "Gratuit",
    areaServed: ["France", "Europe"]
  },
  {
    name: "Account Management Amazon",
    description: "Gestion complète de votre compte Amazon par des experts certifiés SPN : catalogue, campagnes, optimisations",
    serviceType: "Amazon Management",
    priceRange: "€€€",
    areaServed: ["France", "Europe"]
  },
  {
    name: "Amazon Ads & DSP",
    description: "Gestion campagnes publicitaires Amazon (SP/SB/SD/DSP) avec optimisation continue et reporting avancé",
    serviceType: "Amazon Advertising",
    priceRange: "€€",
    areaServed: ["France", "Europe"]
  },
  {
    name: "A+ Content & Amazon Store",
    description: "Création contenus premium A+ et design de Store Amazon pour booster conversions et branding",
    serviceType: "Amazon Creative",
    priceRange: "€€",
    areaServed: ["France", "Europe"]
  },
  {
    name: "Reinstatement & Compliance",
    description: "Support réinstatement compte, résolution suspensions et mise en conformité marketplace Amazon",
    serviceType: "Amazon Compliance",
    priceRange: "€€€",
    areaServed: ["France", "Europe"]
  },
  {
    name: "Ops FBA/FBM",
    description: "Optimisation opérationnelle FBA/FBM, gestion stock, prévisions demande et logistique Amazon",
    serviceType: "Amazon Operations",
    priceRange: "€€",
    areaServed: ["France", "Europe"]
  }
];

// FAQ pour la page Services
const serviceFAQs: FAQ[] = [
  {
    question: "Comment reprendre la Buy Box de manière durable ?",
    answer: "On contrôle d'abord les fondamentaux : prix net + frais, disponibilité (FBA/FBM), délais & taux de défaut. Ensuite, on assainit la santé compte (ODR/LSR), on aligne la politique de prix par canal et on met en place un plan PPC défensif. Résultat attendu : meilleur taux d'éligibilité et stabilité de la Buy Box."
  },
  {
    question: "Mon ASIN a été supprimé ou restreint : que faites-vous ?",
    answer: "Audit rapide des causes (conformité, sécurité client, propriété intellectuelle), collecte des preuves (COA, MSDS, factures) et rédaction d'un Plan d'Action structuré. Nous gérons les échanges Seller Support/Performance jusqu'à la réactivation ou le re-listing conforme."
  },
  {
    question: "ACoS vs ROAS : lequel dois-je piloter ?",
    answer: "Sur Amazon Ads, l'ACoS mesure le coût pub rapporté au chiffre d'affaires ; le ROAS mesure l'inverse. Nous pilotons les deux, avec des cibles par campagne (marque, défense, conquête) et un suivi de la marge nette pour arbitrer les enchères."
  },
  {
    question: "FBA, FBM ou SFP : quel mode logistique choisir ?",
    answer: "FBA maximise la conversion et la visibilité Prime. FBM convient aux catalogues lourds ou spécifiques. SFP est hybride (Prime géré par le vendeur). Nous choisissons par SKU selon coûts, délais et taux de rupture, puis nous testons pour valider la marge réelle."
  },
  {
    question: "Combien de temps pour un A+ et un Store prêt à convertir ?",
    answer: "A+ : 5–10 jours ouvrés selon le volume médias et validations. Store : 1–2 semaines avec la structure des pages, modules et tracking. Les contenus sont SEO Amazon-ready (mots-clés, alt, modules comparatifs) et alignés aux guidelines."
  }
];

export default function Services() {
  const { handleCtaClick, resolveCta } = useCta();

  const summary = "Services Amazon complets : audit gratuit SP-API, gestion Ads (SP/SB/SD/DSP), SEO listings & A+, Amazon Stores, catalogue health et expansion Europe avec approche data-driven.";
  
  const bullets = [
    "Audit Amazon 360° gratuit avec analyse SP-API complète",
    "Gestion campagnes Ads (SP/SB/SD/DSP) et optimisations SEO", 
    "Création A+ Content, Amazon Stores et stratégie visuelle",
    "Catalogue health, Buy Box optimization et expansion EU"
  ];

  return (
    <>
      <AdvancedSEOHead
        title={pageSEO.services.title}
        description={pageSEO.services.description}
        canonical={pageSEO.services.canonical}
        ogImage="/og/services-amazon-1200x630.jpg"
        ogType="website"
        keywords={pageSEO.services.keywords}
        breadcrumbs={[
          { name: "Services", url: "/services" }
        ]}
      />

      {/* Services structured data with FAQs */}
      <StructuredData 
        services={servicesData}
        faqs={serviceFAQs}
        enableOrganization={false}
        enableWebsite={false}
      />

      <main className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          {/* Summary Block */}
          <SummaryBlock summary={summary} bullets={bullets} className="summary-chip" />
          
          {/* Hero Section */}
          <section className="text-center py-16">
            <PartnerBadge variant="light" size="md" className="mb-6" />
            <h1 className="text-5xl font-bold tracking-tight text-foreground mb-6">
              Services Amazon
              <span className="block text-primary mt-2">Expert & Data-Driven</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              De l'audit gratuit à la gestion complète, nos services Amazon couvrent tous vos besoins e-commerce avec une approche data-driven et créative.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="group" onClick={(e) => handleCtaClick('audit', e)}>
                Audit Gratuit
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to={resolveCta('results')}>Voir nos résultats</Link>
              </Button>
            </div>
          </section>

          {/* Services Grid */}
          <ServicesTriad />

          {/* FAQ Section */}
          <section className="py-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Questions Fréquentes</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Réponses d'experts aux questions les plus posées sur nos services Amazon
              </p>
            </div>

            <div className="max-w-4xl mx-auto space-y-6">
              {serviceFAQs.map((faq, index) => (
                <Card key={index} className="p-6">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg font-semibold flex items-start">
                      <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5 flex-shrink-0">
                        {index + 1}
                      </span>
                      {faq.question}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-muted-foreground leading-relaxed ml-9">
                      {faq.answer}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center mt-12">
              <Button size="lg" asChild>
                <Link to="/faq">Voir la FAQ complète</Link>
              </Button>
            </div>
          </section>

          {/* Performance Metrics */}
          <section className="py-16 bg-muted/30 rounded-3xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Résultats Moyens Clients</h2>
              <p className="text-xl text-muted-foreground">
                Performance mesurée sur les 12 derniers mois
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="h-8 w-8 text-primary" />
                </div>
                <div className="text-3xl font-bold text-primary mb-2">+200%</div>
                <div className="text-muted-foreground">Croissance CA</div>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="h-8 w-8 text-primary" />
                </div>
                <div className="text-3xl font-bold text-primary mb-2">-40%</div>
                <div className="text-muted-foreground">Réduction CPC</div>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-primary" />
                </div>
                <div className="text-3xl font-bold text-primary mb-2">80%</div>
                <div className="text-muted-foreground">Taux Buy Box</div>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="h-8 w-8 text-primary" />
                </div>
                <div className="text-3xl font-bold text-primary mb-2">400%</div>
                <div className="text-muted-foreground">ROI Moyen</div>
              </div>
            </div>
          </section>

          {/* CTA Final */}
          <section className="text-center py-16">
            <h2 className="text-3xl font-bold mb-4">Prêt à booster vos ventes Amazon ?</h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Commencez par un audit gratuit et découvrez le potentiel de votre compte Amazon
            </p>
            <Button size="lg" className="group" onClick={(e) => handleCtaClick('audit', e)}>
              Réserver mon audit gratuit
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </section>
        </div>
      </main>
    </>
  );
}